import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { setCredentials } from "./authSlice";

interface ButtonPosition {
  x: number;
  y: number;
  width: number;
}

interface UserActionsState {
  isEditProfileOpen: boolean;
  isRevenuesAndPointsViewerOpen: boolean;
  isCouponsAndPointsViewerOpen: boolean;
  isChangePasswordOpen: boolean;
  isReceiptUploadFormOpen: boolean;
  isQRCodeViewerOpen: boolean;
  isCardRequest: boolean;
  isLoginModalOpen: boolean;
  isRegisterModalOpen: boolean;
  isPasswordRecoveryModalOpen: boolean;
  isActivateExistingAccountModalOpen: boolean; // Neu: Modal für die Aktivierung eines existierenden Kontos
  showActivatedPromotions: boolean;
  activatedCoupons: string[];
  editProfileButtonPosition: ButtonPosition | null;
  revenuesAndPointsButtonPosition: ButtonPosition | null;
  couponsAndPointsButtonPosition: ButtonPosition | null;
  changePasswordButtonPosition: ButtonPosition | null;
  receiptUploadFormButtonPosition: ButtonPosition | null;
  qrCodeViewerButtonPosition: ButtonPosition | null;
  walletLink: string | null;
}

const initialState: UserActionsState = {
  isEditProfileOpen: false,
  isRevenuesAndPointsViewerOpen: false,
  isCouponsAndPointsViewerOpen: false,
  isChangePasswordOpen: false,
  isReceiptUploadFormOpen: false,
  isQRCodeViewerOpen: false,
  isCardRequest: false,
  isLoginModalOpen: false,
  isRegisterModalOpen: false,
  isPasswordRecoveryModalOpen: false,
  isActivateExistingAccountModalOpen: false, // Neu: Initial false
  showActivatedPromotions: false,
  activatedCoupons: [],
  editProfileButtonPosition: null,
  revenuesAndPointsButtonPosition: null,
  couponsAndPointsButtonPosition: null,
  changePasswordButtonPosition: null,
  receiptUploadFormButtonPosition: null,
  qrCodeViewerButtonPosition: null,
  walletLink: null,
};

const userPersistConfig = {
  key: "userActions",
  storage,
  whitelist: ["isEditProfileOpen", "isRevenuesAndPointsViewerOpen", "isCouponsAndPointsViewerOpen", "isChangePasswordOpen", "isReceiptUploadFormOpen", "isQRCodeViewerOpen", "isCardRequest", "isLoginModalOpen", "isRegisterModalOpen", "isPasswordRecoveryModalOpen", "isActivateExistingAccountModalOpen", "activatedCoupons", "walletLink", "showActivatedPromotions"], 
};

export const userActionsSlice = createSlice({
  name: "userActions",
  initialState,
  reducers: {
    // Close all components helper
    closeAllComponents: (state) => {
      state.isEditProfileOpen = false;
      state.isRevenuesAndPointsViewerOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isReceiptUploadFormOpen = false;
      state.isQRCodeViewerOpen = false;
      state.isLoginModalOpen = false;
      state.isRegisterModalOpen = false;
      state.isPasswordRecoveryModalOpen = false;
      state.isActivateExistingAccountModalOpen = false;
    },
    
    // Wallet Link actions
    setWalletLink: (state, action: PayloadAction<string | null>) => {
      state.walletLink = action.payload;
    },
    
    addCardToWallet: () => {
      // Diese Action wird vom CardItem-Component ausgelöst
      // Der eigentliche Link wird vom useInsertToWalletLink-Hook gesetzt
      // Hier können wir optional zusätzliche State-Änderungen vornehmen
    },
    
    clearWalletLink: (state) => {
      state.walletLink = null;
    },
    
    // EditProfile actions
    toggleEditProfile: (state, action: PayloadAction<ButtonPosition | undefined>) => {
      // Close all components first
      state.isRevenuesAndPointsViewerOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isReceiptUploadFormOpen = false;
      state.isQRCodeViewerOpen = false;
      // Then toggle the edit profile component
      state.isEditProfileOpen = !state.isEditProfileOpen;
      // Reset the card request flag
      state.isCardRequest = false;
      // Store button position if provided
      if (action.payload) {
        state.editProfileButtonPosition = action.payload;
      }
    },
    openEditProfile: (state, action: PayloadAction<ButtonPosition | undefined>) => {
      // Close all components first
      state.isRevenuesAndPointsViewerOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isReceiptUploadFormOpen = false;
      state.isQRCodeViewerOpen = false;
      // Then open the target component
      state.isEditProfileOpen = true;
      // Reset the card request flag
      state.isCardRequest = false;
      // Store button position if provided
      if (action.payload) {
        state.editProfileButtonPosition = action.payload;
      }
    },
    closeEditProfile: (state) => {
      state.isEditProfileOpen = false;
    },
    
    // RevenuesAndPointsViewer actions
    toggleRevenuesAndPointsViewer: (state, action: PayloadAction<ButtonPosition | undefined>) => {
      // Close all components first
      state.isEditProfileOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isReceiptUploadFormOpen = false;
      state.isQRCodeViewerOpen = false;
      // Then toggle the target component
      state.isRevenuesAndPointsViewerOpen = !state.isRevenuesAndPointsViewerOpen;
      // Store button position if provided
      if (action.payload) {
        state.revenuesAndPointsButtonPosition = action.payload;
      }
    },
    openRevenuesAndPointsViewer: (state, action: PayloadAction<ButtonPosition | undefined>) => {
      // Close all components first
      state.isEditProfileOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isReceiptUploadFormOpen = false;
      state.isQRCodeViewerOpen = false;
      // Then open the target component
      state.isRevenuesAndPointsViewerOpen = true;
      // Store button position if provided
      if (action.payload) {
        state.revenuesAndPointsButtonPosition = action.payload;
      }
    },
    closeRevenuesAndPointsViewer: (state) => {
      state.isRevenuesAndPointsViewerOpen = false;
    },

    // CouponsAndPointsViewer actions
    toggleCouponsAndPointsViewer: (state, action: PayloadAction<ButtonPosition | undefined>) => {
      // Close all components first
      state.isEditProfileOpen = false;
      state.isRevenuesAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isReceiptUploadFormOpen = false;
      state.isQRCodeViewerOpen = false;
      // Then toggle the target component
      state.isCouponsAndPointsViewerOpen = !state.isCouponsAndPointsViewerOpen;
      // Store button position if provided
      if (action.payload) {
        state.couponsAndPointsButtonPosition = action.payload;
      }
    },
    openCouponsAndPointsViewer: (state, action: PayloadAction<ButtonPosition | undefined>) => {
      // Close all components first
      state.isEditProfileOpen = false;
      state.isRevenuesAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isReceiptUploadFormOpen = false;
      state.isQRCodeViewerOpen = false;
      // Then open the target component
      state.isCouponsAndPointsViewerOpen = true;
      // Store button position if provided
      if (action.payload) {
        state.couponsAndPointsButtonPosition = action.payload;
      }
    },
    closeCouponsAndPointsViewer: (state) => {
      state.isCouponsAndPointsViewerOpen = false;
    },

    // ChangePassword actions
    toggleChangePassword: (state, action: PayloadAction<ButtonPosition | undefined>) => {
      // Close all components first
      state.isEditProfileOpen = false;
      state.isRevenuesAndPointsViewerOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isReceiptUploadFormOpen = false;
      state.isQRCodeViewerOpen = false;
      // Then toggle the target component
      state.isChangePasswordOpen = !state.isChangePasswordOpen;
      // Store button position if provided
      if (action.payload) {
        state.changePasswordButtonPosition = action.payload;
      }
    },
    openChangePassword: (state, action: PayloadAction<ButtonPosition | undefined>) => {
      // Close all components first
      state.isEditProfileOpen = false;
      state.isRevenuesAndPointsViewerOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isReceiptUploadFormOpen = false;
      state.isQRCodeViewerOpen = false;
      // Then open the target component
      state.isChangePasswordOpen = true;
      // Store button position if provided
      if (action.payload) {
        state.changePasswordButtonPosition = action.payload;
      }
    },
    closeChangePassword: (state) => {
      state.isChangePasswordOpen = false;
    },

    // ReceiptUploadForm actions
    toggleReceiptUploadForm: (state, action: PayloadAction<ButtonPosition | undefined>) => {
      // Close all components first
      state.isEditProfileOpen = false;
      state.isRevenuesAndPointsViewerOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isQRCodeViewerOpen = false;
      // Then toggle the target component
      state.isReceiptUploadFormOpen = !state.isReceiptUploadFormOpen;
      // Store button position if provided
      if (action.payload) {
        state.receiptUploadFormButtonPosition = action.payload;
      }
    },
    openReceiptUploadForm: (state, action: PayloadAction<ButtonPosition | undefined>) => {
      // Close all components first
      state.isEditProfileOpen = false;
      state.isRevenuesAndPointsViewerOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isQRCodeViewerOpen = false;
      // Then open the target component
      state.isReceiptUploadFormOpen = true;
      // Store button position if provided
      if (action.payload) {
        state.receiptUploadFormButtonPosition = action.payload;
      }
    },
    closeReceiptUploadForm: (state) => {
      state.isReceiptUploadFormOpen = false;
    },

    // QRCodeViewer actions
    toggleQRCodeViewer: (state, action: PayloadAction<ButtonPosition | undefined>) => {
      // Close all components first
      state.isEditProfileOpen = false;
      state.isRevenuesAndPointsViewerOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isReceiptUploadFormOpen = false;
      // Then toggle the target component
      state.isQRCodeViewerOpen = !state.isQRCodeViewerOpen;
      // Store button position if provided
      if (action.payload) {
        state.qrCodeViewerButtonPosition = action.payload;
      }
    },
    openQRCodeViewer: (state, action: PayloadAction<ButtonPosition | undefined>) => {
      // Close all components first
      state.isEditProfileOpen = false;
      state.isRevenuesAndPointsViewerOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isReceiptUploadFormOpen = false;
      // Then open the target component
      state.isQRCodeViewerOpen = true;
      // Store button position if provided
      if (action.payload) {
        state.qrCodeViewerButtonPosition = action.payload;
      }
    },
    closeQRCodeViewer: (state) => {
      state.isQRCodeViewerOpen = false;
    },

    // Request new card action
    getNewCardToggle: (state, action: PayloadAction<ButtonPosition | undefined>) => {
      // Close all components first
      state.isRevenuesAndPointsViewerOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isReceiptUploadFormOpen = false;
      state.isQRCodeViewerOpen = false;
      // Then open the edit profile component
      state.isEditProfileOpen = true;
      // Set the card request flag
      state.isCardRequest = true;
      // Store button position if provided
      if (action.payload) {
        state.editProfileButtonPosition = action.payload;
      }
    },
    
    // Coupon activation actions
    setCouponActivated: (state, action: PayloadAction<{ couponId: string; activated: boolean }>) => {
      const { couponId, activated } = action.payload;
      if (activated) {
        // Füge die Coupon-ID hinzu, wenn sie noch nicht im Array ist
        if (!state.activatedCoupons.includes(couponId)) {
          state.activatedCoupons.push(couponId);
        }
      } else {
        // Entferne die Coupon-ID aus dem Array
        state.activatedCoupons = state.activatedCoupons.filter(id => id !== couponId);
      }
    },
    
    activateCoupon: (state, action: PayloadAction<string>) => {
      const couponId = action.payload;
      if (!state.activatedCoupons.includes(couponId)) {
        state.activatedCoupons.push(couponId);
      }
    },
    
    resetCouponActivation: (state) => {
      state.activatedCoupons = [];
    },
    
    deactivateCoupon: (state, action: PayloadAction<string>) => {
      const couponId = action.payload;
      state.activatedCoupons = state.activatedCoupons.filter(id => id !== couponId);
    },
    
    // Login Modal actions
    toggleLoginModal: (state) => {
      // Close all components first
      state.isEditProfileOpen = false;
      state.isRevenuesAndPointsViewerOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isReceiptUploadFormOpen = false;
      state.isQRCodeViewerOpen = false;
      state.isRegisterModalOpen = false;
      state.isPasswordRecoveryModalOpen = false;
      // Then toggle the login modal
      state.isLoginModalOpen = !state.isLoginModalOpen;
    },
    openLoginModal: (state) => {
      // Close all components first
      state.isEditProfileOpen = false;
      state.isRevenuesAndPointsViewerOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isReceiptUploadFormOpen = false;
      state.isQRCodeViewerOpen = false;
      state.isRegisterModalOpen = false;
      state.isPasswordRecoveryModalOpen = false;
      // Then open the login modal
      state.isLoginModalOpen = true;
    },
    closeLoginModal: (state) => {
      state.isLoginModalOpen = false;
    },
    
    // Register Modal actions
    toggleRegisterModal: (state) => {
      // Close all components first
      state.isEditProfileOpen = false;
      state.isRevenuesAndPointsViewerOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isReceiptUploadFormOpen = false;
      state.isQRCodeViewerOpen = false;
      state.isLoginModalOpen = false;
      state.isPasswordRecoveryModalOpen = false;
      // Then toggle the register modal
      state.isRegisterModalOpen = !state.isRegisterModalOpen;
    },
    openRegisterModal: (state) => {
      // Close all components first
      state.isEditProfileOpen = false;
      state.isRevenuesAndPointsViewerOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isReceiptUploadFormOpen = false;
      state.isQRCodeViewerOpen = false;
      state.isLoginModalOpen = false;
      state.isPasswordRecoveryModalOpen = false;
      // Then open the register modal
      state.isRegisterModalOpen = true;
    },
    closeRegisterModal: (state) => {
      state.isRegisterModalOpen = false;
    },
    
    // Password Recovery Modal actions
    togglePasswordRecoveryModal: (state) => {
      // Close all components first
      state.isEditProfileOpen = false;
      state.isRevenuesAndPointsViewerOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isReceiptUploadFormOpen = false;
      state.isQRCodeViewerOpen = false;
      state.isLoginModalOpen = false;
      state.isRegisterModalOpen = false;
      // Then toggle the password recovery modal
      state.isPasswordRecoveryModalOpen = !state.isPasswordRecoveryModalOpen;
    },
    openPasswordRecoveryModal: (state) => {
      // Close all components first
      state.isEditProfileOpen = false;
      state.isRevenuesAndPointsViewerOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isReceiptUploadFormOpen = false;
      state.isQRCodeViewerOpen = false;
      state.isLoginModalOpen = false;
      state.isRegisterModalOpen = false;
      // Then open the password recovery modal
      state.isPasswordRecoveryModalOpen = true;
    },
    closePasswordRecoveryModal: (state) => {
      state.isPasswordRecoveryModalOpen = false;
    },
    
    // Activate Existing Account Modal actions
    toggleActivateExistingAccountModal: (state) => {
      // Close all components first
      state.isEditProfileOpen = false;
      state.isRevenuesAndPointsViewerOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isReceiptUploadFormOpen = false;
      state.isQRCodeViewerOpen = false;
      state.isLoginModalOpen = false;
      state.isRegisterModalOpen = false;
      state.isPasswordRecoveryModalOpen = false;
      // Then toggle the activate existing account modal
      state.isActivateExistingAccountModalOpen = !state.isActivateExistingAccountModalOpen;
    },
    openActivateExistingAccountModal: (state) => {
      // Close all components first
      state.isEditProfileOpen = false;
      state.isRevenuesAndPointsViewerOpen = false;
      state.isCouponsAndPointsViewerOpen = false;
      state.isChangePasswordOpen = false;
      state.isReceiptUploadFormOpen = false;
      state.isQRCodeViewerOpen = false;
      state.isLoginModalOpen = false;
      state.isRegisterModalOpen = false;
      state.isPasswordRecoveryModalOpen = false;
      // Then open the activate existing account modal
      state.isActivateExistingAccountModalOpen = true;
    },
    closeActivateExistingAccountModal: (state) => {
      state.isActivateExistingAccountModalOpen = false;
    },
    
    // Toggle für die Anzeige nur aktivierter Promotions
    toggleShowActivatedPromotions: (state) => {
      state.showActivatedPromotions = !state.showActivatedPromotions;
    },
    
  },
  extraReducers: (builder) => {
    builder.addCase(setCredentials, (state) => {
      state.isActivateExistingAccountModalOpen = false;
    });
  },
});

export const { 
  toggleEditProfile, 
  openEditProfile, 
  closeEditProfile,
  toggleRevenuesAndPointsViewer,
  openRevenuesAndPointsViewer,
  closeRevenuesAndPointsViewer,
  toggleCouponsAndPointsViewer,
  openCouponsAndPointsViewer,
  closeCouponsAndPointsViewer,
  toggleChangePassword,
  openChangePassword,
  closeChangePassword,
  toggleReceiptUploadForm,
  openReceiptUploadForm,
  closeReceiptUploadForm,
  toggleQRCodeViewer,
  openQRCodeViewer,
  closeQRCodeViewer,
  closeAllComponents,
  getNewCardToggle,
  setWalletLink,
  addCardToWallet,
  clearWalletLink,
  setCouponActivated,
  activateCoupon,
  resetCouponActivation,
  deactivateCoupon,
  toggleLoginModal,
  openLoginModal,
  closeLoginModal,
  toggleRegisterModal,
  openRegisterModal,
  closeRegisterModal,
  togglePasswordRecoveryModal,
  openPasswordRecoveryModal,
  closePasswordRecoveryModal,
  toggleActivateExistingAccountModal,
  openActivateExistingAccountModal,
  closeActivateExistingAccountModal,
  toggleShowActivatedPromotions
} = userActionsSlice.actions;

export default persistReducer(userPersistConfig, userActionsSlice.reducer);
